// Sons rétro (chiptune) synthétisés via Web Audio — aucun fichier à charger.
// L'AudioContext est créé paresseusement et repris au 1ᵉʳ geste utilisateur
// (politique d'autoplay mobile/Telegram). Coupable via setSoundEnabled().

const KEY = "the-big-dig.sound";
let enabled = true;
try {
  enabled = localStorage.getItem(KEY) !== "off";
} catch {
  /* stockage indisponible */
}

let ctx: AudioContext | null = null;
function context(): AudioContext | null {
  if (!enabled) return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

interface Note {
  f: number; // fréquence (Hz)
  t?: number; // décalage de départ (s)
  d?: number; // durée (s)
  type?: OscillatorType;
  v?: number; // volume crête (0–1)
  slide?: number; // glissando vers cette fréquence
}

function play(notes: Note[]): void {
  const c = context();
  if (!c) return;
  const base = c.currentTime;
  for (const n of notes) {
    const t0 = base + (n.t ?? 0);
    const d = n.d ?? 0.1;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = n.type ?? "square";
    osc.frequency.setValueAtTime(n.f, t0);
    if (n.slide) osc.frequency.exponentialRampToValueAtTime(Math.max(40, n.slide), t0 + d);
    const v = n.v ?? 0.15;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(v, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + d);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + d + 0.03);
  }
}

// Notes de réussite par précision (1 OK / 2 BIEN / 3 PARFAIT) : C5 / E5 / G5.
const PREC_FREQ: Record<number, number> = { 1: 523.25, 2: 659.25, 3: 783.99 };

export const sfx = {
  /** Amorce du coup (clic au bon moment) : tick discret. */
  poke(): void {
    play([{ f: 880, d: 0.04, v: 0.06 }]);
  },
  /** Raté : buzz descendant. */
  miss(): void {
    play([{ f: 196, slide: 104, d: 0.22, v: 0.17, type: "sawtooth" }]);
  },
  /** Réussite à l'impact : aigu selon précision + combo (arpège si BIEN/PARFAIT). */
  score(precision: number, combo: number): void {
    let f = PREC_FREQ[precision] ?? PREC_FREQ[1]!;
    f *= 1 + Math.min(Math.max(combo - 1, 0), 7) * 0.04; // le combo monte un peu le pitch
    const notes: Note[] = [{ f, d: 0.09, v: 0.16 }];
    if (precision >= 3) notes.push({ f: f * 1.5, t: 0.07, d: 0.12, v: 0.14 });
    else if (precision >= 2) notes.push({ f: f * 1.25, t: 0.06, d: 0.09, v: 0.12 });
    play(notes);
  },
  /** Fin de partie (perdu) : descente « sad ». */
  over(): void {
    play([
      { f: 392, d: 0.16, v: 0.16 },
      { f: 311, t: 0.16, d: 0.16, v: 0.16 },
      { f: 233, t: 0.32, d: 0.32, v: 0.16 },
    ]);
  },
  /** Victoire (objectif de série atteint) : jingle montant. */
  win(): void {
    play([
      { f: 523, d: 0.1 },
      { f: 659, t: 0.1, d: 0.1 },
      { f: 784, t: 0.2, d: 0.1 },
      { f: 1047, t: 0.3, d: 0.22, v: 0.17 },
    ]);
  },
  /** Nouveau record : arpège scintillant. */
  record(): void {
    play([
      { f: 784, d: 0.07, type: "triangle", v: 0.15 },
      { f: 988, t: 0.07, d: 0.07, type: "triangle", v: 0.15 },
      { f: 1319, t: 0.14, d: 0.07, type: "triangle", v: 0.15 },
      { f: 1047, t: 0.22, d: 0.2, type: "triangle", v: 0.16 },
    ]);
  },
  /** Navigation menu (▲▼). */
  nav(): void {
    play([{ f: 660, d: 0.03, v: 0.05 }]);
  },
  /** Validation (A). */
  select(): void {
    play([
      { f: 660, d: 0.04, v: 0.09 },
      { f: 990, t: 0.04, d: 0.06, v: 0.09 },
    ]);
  },
  /** Retour (B). */
  back(): void {
    play([{ f: 440, slide: 320, d: 0.05, v: 0.08 }]);
  },
};

/** Active/coupe le son (persisté). */
export function setSoundEnabled(on: boolean): void {
  enabled = on;
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    /* ignore */
  }
}

export function isSoundEnabled(): boolean {
  return enabled;
}
